import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AxiosError } from 'axios';
import type { UserInfoDto } from '@features/authentication/types';
import { authService, tokenService } from '@features/authentication/services';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

interface AuthState {
  user: UserInfoDto | null;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  clearError: () => void;
  clearMustChangePassword: () => void;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user : null,
      isAuthenticated: false,
      mustChangePassword: false,
      isLoading: false,
      error: null,

      login: async (usernameOrEmail, password) => {
        set({ isLoading: true, error: null });
        try {
          const authData = await authService.login({ usernameOrEmail, password });
          const user = await authService.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            mustChangePassword: authData.mustChangePassword,
            isLoading: false,
          });
        } catch (error) {
          const message = getApiErrorMessage(error, 'Login failed');
          set({
            error: message,
            isLoading: false,
            user: null,
            isAuthenticated: false,
            mustChangePassword: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
        } finally {
          set({ user: null, isAuthenticated: false, mustChangePassword: false, isLoading: false });
        }
      },

      fetchCurrentUser: async () => {
        if (!tokenService.getAccessToken()) {
          set({ user: null, isAuthenticated: false, mustChangePassword: false });
          return;
        }

        set({ isLoading: true });
        try {
          const user = await authService.getCurrentUser();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (error) {
          // Only clear tokens on 401 (auth failure the interceptor couldn't recover from).
          // Transient errors (network, 500) should not destroy the session.
          const is401 = error instanceof AxiosError && error.response?.status === 401;
          if (is401) {
            // endSession, not clearTokens (86d3zp17w): reaching here means the interceptor already
            // tried to refresh and could not, so this is a genuine end and siblings must be told
            // explicitly rather than inferring it from a key vanishing.
            tokenService.endSession('expired');
            set({ user: null, isAuthenticated: false, mustChangePassword: false, isLoading: false });
          } else {
            set({ isLoading: false });
          }
        }
      },

      clearError: () => set({ error: null }),

      clearMustChangePassword: () => set({ mustChangePassword: false }),

      hasRole: (role: string) => {
        const { user } = get();
        return user?.roles.includes(role) ?? false;
      },

      hasPermission: (permission: string) => {
        const { user } = get();
        return user?.permissions?.includes(permission) ?? false;
      },
    }),
    {
      name: 'csc-wms-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        mustChangePassword: state.mustChangePassword,
      }),
    }
  )
);
